'use client';
import React, { useRef, useState, useEffect } from 'react';

function preload(src) {
  return new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => i.decode?.().catch(() => {
    }).finally(() => resolve());
    i.onerror = () => reject();
    i.src = src;
  });
}
function RevealWhenReadyEnhancer({ children, style }) {
  const containerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [errored, setErrored] = useState(false);
  const [inlineSwapActive, setInlineSwapActive] = useState(false);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const img = container.querySelector("img");
    if (!img) return;
    let cancelled = false;
    const finish = () => !cancelled && setReady(true);
    const fail = () => !cancelled && setErrored(true);
    const dataSrc = img.dataset?.src ?? void 0;
    setInlineSwapActive(Boolean(dataSrc));
    if (dataSrc) {
      preload(dataSrc).then(() => {
        if (cancelled) return;
        img.src = dataSrc;
        img.removeAttribute("data-src");
        img.decode?.().catch(() => {
        }).finally(finish);
      }).catch(fail);
      return () => {
        cancelled = true;
      };
    }
    const onLoad = () => {
      img.decode?.().catch(() => {
      }).finally(finish);
    };
    if (img.complete && img.naturalWidth > 0) {
      onLoad();
      return () => {
        cancelled = true;
      };
    } else {
      img.addEventListener("load", onLoad, { once: true });
      img.addEventListener("error", fail, { once: true });
      return () => {
        cancelled = true;
        img.removeEventListener("load", onLoad);
        img.removeEventListener("error", fail);
      };
    }
  }, [children]);
  const invisible = !ready && !errored && !inlineSwapActive;
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      ref: containerRef,
      "aria-busy": !ready ? true : void 0,
      style: {
        opacity: invisible ? 0 : 1,
        visibility: invisible ? "hidden" : "visible",
        pointerEvents: invisible ? "none" : "auto",
        transition: "opacity 180ms ease",
        height: "100%",
        width: "100%",
        ...style
      }
    },
    children
  );
}

export { RevealWhenReadyEnhancer };
